import React, { useEffect } from "react";

const LAUNCH_SESSION_KEY = "lm_launch_seen";

function markLaunchHandled() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LAUNCH_SESSION_KEY, "1");
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
    // Launch behavior must never block access to the app.
  }
}

function wasLaunchHandled() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(LAUNCH_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export default function LaunchScreen({ onComplete }) {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const isTopLevelEntry = pathname === "/" || pathname === "/login";
  const skipAfterSavedInterview = typeof window !== "undefined" &&
    pathname === "/login" &&
    new URLSearchParams(window.location.search).get("interview_saved") === "1";
  const skipForRestoredSession = typeof window !== "undefined" &&
    Boolean(window.__LM_INITIAL_AUTH_USER__);
  const skipLaunch =
    !isTopLevelEntry ||
    wasLaunchHandled() ||
    skipAfterSavedInterview ||
    skipForRestoredSession;

  useEffect(() => {
    // Any deep-link/restored entry counts as this tab's launch having already
    // been handled. That prevents a later redirect to /login from replaying
    // the startup video after password reset, OAuth, report links, etc.
    if (skipLaunch) {
      markLaunchHandled();
      onComplete();
      return;
    }

    // Mark the tab as soon as the genuine startup begins so a refresh while
    // the video is playing cannot trigger it a second time.
    markLaunchHandled();
  }, [skipLaunch, onComplete]);

  if (skipLaunch) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-white"
      data-testid="launch-screen"
      role="status"
      aria-label="Language Masters Software · English Job Interview Simulator"
    >
      <div className="flex w-full max-w-[1152px] max-h-[648px] items-center justify-center px-4">
        <video
          className="h-auto w-full max-h-[648px] object-contain bg-white"
          src="/launch/Introsplash3.mp4"
          autoPlay
          muted
          playsInline
          preload="auto"
          controls={false}
          onEnded={onComplete}
          onError={onComplete}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
